import { Button, SearchField } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useMemo, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";

import { RefreshButton } from "@/components/common/refresh-button";
import { VariantAttributeModal } from "@/components/product/variant-attributes/attribute-form";
import { VariantAttributesTable } from "@/components/product/variant-attributes/attribute-list";
import { RoleLockTooltip } from "@/features/roles/components/role-lock-tooltip";
import {
  useGetVariantAttributes,
  variantAttributeKeys,
} from "@/hooks/api/variant-attributes";
import { useMemoizedCallback } from "@/hooks/useMemorizedCallback";
import { useRoles } from "@/hooks/useRoles";
import { PageLayout } from "@/layouts/page";
import { queryClient } from "@/main";
import { VariantAttribute } from "@/types/product";
import { formatNumber } from "@/utils/number";
import { matchesQuery, pluralize } from "@/utils/string";

export const VariantAttributesPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] =
    useState<VariantAttribute | null>(null);
  // The field stays uncontrolled so typing never re-renders the table body.
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [search, setSearch] = useState("");

  const debouncedSetSearch = useDebounceCallback(setSearch, 300);
  const canEdit = useRoles().can("store.manage");
  const {
    data: attributes,
    isLoading,
    isFetching,
    refetch,
  } = useGetVariantAttributes();

  const countLabel = attributes
    ? `${formatNumber(attributes.length)} ${pluralize(attributes.length, "attribute")}`
    : undefined;

  const filteredAttributes = useMemo(
    () =>
      (attributes ?? []).filter((attribute) =>
        matchesQuery(search, [attribute.name]),
      ),
    [attributes, search],
  );

  const invalidateAttributes = () => {
    queryClient.invalidateQueries({
      queryKey: variantAttributeKeys.lists(),
    });
  };

  const handleOpenCreate = () => {
    setEditingAttribute(null);
    setIsModalOpen(true);
  };

  const handleEditAttribute = useMemoizedCallback(
    (attribute: VariantAttribute) => {
      setEditingAttribute(attribute);
      setIsModalOpen(true);
    },
  );

  const handleClearSearch = useMemoizedCallback(() => {
    debouncedSetSearch.cancel();
    setSearch("");
    setSearchResetKey((key) => key + 1);
  });

  const handleOpenChange = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) setEditingAttribute(null);
  };

  return (
    <PageLayout
      buttons={[
        <RefreshButton
          key="refresh"
          isFetching={isFetching}
          onRefresh={() => void refetch()}
        />,
        <RoleLockTooltip
          key="create"
          action="create attributes"
          isLocked={!canEdit}
        >
          <Button
            isDisabled={!canEdit}
            size="sm"
            variant="primary"
            onPress={handleOpenCreate}
          >
            <Icon fontSize={18} icon="solar:add-linear" />
            Create attribute
          </Button>
        </RoleLockTooltip>,
      ]}
      containerClassName="flex min-h-0 flex-col overflow-hidden pt-2 pb-2"
      title="Variant attributes"
      toolbar={
        <div className="flex items-center gap-3">
          <SearchField
            key={searchResetKey}
            aria-label="Search attributes"
            className="w-66"
            defaultValue=""
            variant="secondary"
            onChange={debouncedSetSearch}
          >
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder="Search attributes" />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
        </div>
      }
    >
      <VariantAttributesTable
        attributes={filteredAttributes}
        countLabel={countLabel}
        isFetching={isLoading}
        search={search}
        onClearSearch={handleClearSearch}
        onEdit={handleEditAttribute}
      />

      <VariantAttributeModal
        editingAttribute={editingAttribute}
        isOpen={isModalOpen}
        onOpenChange={handleOpenChange}
        onSuccess={invalidateAttributes}
      />
    </PageLayout>
  );
};

export default VariantAttributesPage;
